import { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Typography } from "@mui/material";
import { CustomInput } from "../Dynamic/Utils";
import { textClasses } from "../utils/theme";
import { customAPI, debounce } from "../../services/functions";

/**
 * DynamicFilter - Renders refinement filters dynamically from table header attributes.
 *
 * Exposes `getFilters()` via ref — parent calls it to get current dynamic filter values
 * as an array of { path, value } objects ready for the backend.
 *
 * filterConfig shape (same as DynamicForm attributes):
 * {
 *   _id, type, label, placeholder, value, values,
 *   dynamic, searchableDropdown, api, apiMethod,
 *   apiParameters, apiBindingKey, apiBindingValue,
 *   apiDataReturnKey, sequenceNo
 * }
 */
const DynamicFilter = forwardRef(({ attributes = [] }, ref) => {
	// Extract filterConfig from attributes, sorted by sequenceNo
	const filterConfigs = useMemo(() => {
		return attributes
			.filter((attr) => attr.filterConfig && attr.isSearchable)
			.map((attr) => ({
				...attr.filterConfig,
				_parentKey: attr.key || attr.name,
			}))
			.sort((a, b) => (a.sequenceNo ?? 99) - (b.sequenceNo ?? 99));
	}, [attributes]);

	const [filterAttributes, setFilterAttributes] = useState([]);
	const [errors, setErrors] = useState({});
	const [loadingFields, setLoadingFields] = useState({});
	const isInitialised = useRef(false);
	const filterAttrsRef = useRef([]);

	// Keep ref in sync with state so getFilters() always reads latest
	useEffect(() => {
		filterAttrsRef.current = filterAttributes;
	}, [filterAttributes]);

	// Initialize filter attributes only once when configs first arrive
	useEffect(() => {
		if (!filterConfigs.length || isInitialised.current) return;
		isInitialised.current = true;

		const attrs = filterConfigs.map((cfg) => ({
			...cfg,
			value: cfg.value ?? "",
		}));
		setFilterAttributes(attrs);
		filterAttrsRef.current = attrs;
	}, [filterConfigs]);

	// Expose getFilters() and clearFilters() to parent via ref
	useImperativeHandle(ref, () => ({
		getFilters: () => {
			// Fall back to filterConfigs defaults when state hasn't initialized yet
			// (e.g. on navigate-back where cached data triggers parent API call before init effect runs)
			const source = filterAttrsRef.current.length ? filterAttrsRef.current : filterConfigs;
			const filters = [];
			source.forEach((attr) => {
				const val = attr.value ?? "";
				if (val) {
					filters.push({ path: attr._parentKey || attr._id, value: val });
				}
			});
			return filters;
		},
		clearFilters: () => {
			setFilterAttributes((old) => old.map((attr) => ({ ...attr, value: "" })));
		},
	}));

	// --- API fetching for dynamic dropdowns ---

	function fetchSourceDropdownData(field, inputValue = "", cb = null) {
		const { _id, api, apiMethod = "post", apiDataReturnKey = "data", domain } = field;
		if (!api) return;

		let payload = buildAPIPayload(field, inputValue);
		let authInfo = { auth: field.auth, authMethod: field.authMethod, authParams: field.authParams };

		setLoadingFields((o) => ({ ...o, [_id]: true }));
		customAPI(payload, api, apiMethod, domain, authInfo).then((res) => {
			setLoadingFields((o) => ({ ...o, [_id]: false }));
			if (!res.error) {
				let data = res[apiDataReturnKey] || [];
				updateFilterAttr(_id, data, "values");
				if (cb) cb(data);
			} else {
				updateFilterAttr(_id, [], "values");
			}
		});
	}

	function buildAPIPayload(field, searchValue = "") {
		const { apiMethod = "post", apiParameters = [] } = field;
		if (apiMethod.toLowerCase() === "get") {
			return apiParameters
				.map((p) => `${encodeURIComponent(p.label)}=${encodeURIComponent(p.value === "onSearch" ? searchValue : p.value)}`)
				.join("&");
		}
		let payload = {};
		apiParameters.forEach((p) => {
			payload[p.label] = p.value === "onSearch" ? searchValue : p.value;
		});
		return payload;
	}

	function updateFilterAttr(id, data, key = "values") {
		setFilterAttributes((old) =>
			old.map((attr) => (attr._id === id ? { ...attr, [key]: data } : attr))
		);
	}

	const handleDropdownSearch = useCallback(
		debounce((field, inputValue) => {
			fetchSourceDropdownData(field, inputValue);
		}, 500),
		[]
	);

	// --- Cascading / dependent dropdown logic ---

	function buildTargetPayload(field) {
		const { targetAPIMethod = "post", targetParameters = [], value = "" } = field;
		const method = targetAPIMethod.toLowerCase();

		if (method === "get") {
			return targetParameters
				.map((p) => {
					const paramValue = p.value === "self" ? value : p.value;
					return `${encodeURIComponent(p.label)}=${encodeURIComponent(paramValue)}`;
				})
				.join("&");
		}

		let payload = {};
		targetParameters.forEach((p) => {
			if (p.value === "self") {
				payload[p.label] = value;
			} else if (typeof p.value === "string" && p.value.includes("$target")) {
				// eg: "$target.fieldId#self"
				const targetPath = p.value.split("#")[0].split(".").splice(1).join(".");
				const targetField = filterAttrsRef.current.find((a) => a._id === targetPath);
				if (targetField) payload[p.label] = targetField.value;
			} else {
				payload[p.label] = p.value;
			}
		});
		return payload;
	}

	function handleSourceSelectionCases(field) {
		const { bindTarget, setTargetValue, targetAPI, targetAPIMethod, targetElement, targetDomain, domain, _id } = field;

		if (!bindTarget && !(setTargetValue && targetAPI)) return;
		if (!targetAPI || !targetElement) return;

		const payload = buildTargetPayload(field);
		const authInfo = { auth: field.targetAuth, authMethod: field.targetAuthMethod, authParams: field.targetAuthParams };

		setLoadingFields((o) => ({ ...o, [targetElement]: true }));
		customAPI(payload, targetAPI, targetAPIMethod, targetDomain || domain, authInfo).then((res) => {
			setLoadingFields((o) => ({ ...o, [targetElement]: false }));
			if (!res.error) {
				const returnKey = field.targetDataReturnKey || "data";
				updateFilterAttr(targetElement, res[returnKey] || [], "values");
				// clear the target's current value since parent changed
				updateFilterAttr(targetElement, "", "value");
				// merge payload params into target's apiParameters so search includes them
				const newApiParams = Object.keys(payload).map((key) => ({ label: key, value: payload[key] }));
				setFilterAttributes((old) =>
					old.map((attr) => {
						if (attr._id === targetElement) {
							// remove old injected params with same keys, then add new ones
							const existingLabels = new Set(newApiParams.map((p) => p.label));
							const cleaned = (attr.apiParameters || []).filter((p) => !existingLabels.has(p.label));
							return { ...attr, apiParameters: [...cleaned, ...newApiParams] };
						}
						return attr;
					})
				);
			} else {
				updateFilterAttr(targetElement, [], "values");
				updateFilterAttr(targetElement, "", "value");
			}
		});
	}

	// --- Value change handlers ---

	const handleDropdownChange = (field, newVal) => {
		updateFilterAttr(field._id, newVal, "value");
		handleSourceSelectionCases({ ...field, value: newVal });
	};

	const handleInputChange = (field, value) => {
		updateFilterAttr(field._id, value, "value");
	};

	if (!filterAttributes.length) return null;

	return (
		<>
			{filterAttributes.map((component) => (
				<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.25rem" }} key={component._id}>
					<CustomInput
						component={component}
						errors={errors}
						fetchSourceDropdownData={fetchSourceDropdownData}
						onDropdownChange={(newVal) => {
							handleDropdownChange(component, newVal);
						}}
						onInputChange={(e) => {
							handleInputChange(component, e.target.value);
						}}
						onQuickAddSave={() => {}}
						onFetchBtnClick={() => {}}
						onTableRowSelect={() => {}}
						loadingFields={loadingFields}
						filterOnly={true}
					/>
					<Typography sx={{ ...textClasses.t12n, color: "#e74c3c", textAlign: "left", mt: "5px", ml: "5px" }}>
						{errors[component._id] || ""}
					</Typography>
				</div>
			))}
		</>
	);
});

DynamicFilter.displayName = "DynamicFilter";

export default DynamicFilter;
